import Swiper from 'swiper'
import { SwiperOptions } from 'swiper/types/swiper-options'
import { Navigation, Pagination, Autoplay } from 'swiper/modules'

const createNavigation = (element: HTMLElement) => {
    const navigation: HTMLElement = document.createElement('nav')
    navigation.classList.add('swiper-navigation')
    element.appendChild(navigation)

    const buttonPrev: HTMLButtonElement = document.createElement('button')
    buttonPrev.classList.add('swiper-navigation__button')
    buttonPrev.classList.add('swiper-navigation__button-prev')
    buttonPrev.ariaLabel = `{{js.swiper.navigation.slide.previous.text}}`

    const buttonNext: HTMLButtonElement = document.createElement('button')
    buttonNext.classList.add('swiper-navigation__button')
    buttonNext.classList.add('swiper-navigation__button-next')
    buttonNext.ariaLabel = `{{js.swiper.navigation.slide.next.text}}`

    navigation.appendChild(buttonPrev)
    navigation.appendChild(buttonNext)
}

const createPagination = (element: HTMLElement) => {
    const pagination: HTMLElement = document.createElement('nav')
    pagination.classList.add('swiper-pagination')
    element.appendChild(pagination)
}

const createCallToAction = (swiper: Swiper) => {
    const container = document.createElement('div')
    container.className = 'swiper-cta'

    const buttons = swiper.el.querySelectorAll<HTMLAnchorElement>('.swiper-slide.attrax-button')
    buttons.forEach((button) => {
        button.remove()
        button.classList.remove('swiper-slide')
        button.removeAttribute('style')
        button.removeAttribute('tabindex')
        button.href = button.href.toLowerCase()
        container.appendChild(button)
    })

    swiper.update()
    swiper.el.appendChild(container)
}

const createWrapper = (element: HTMLElement) => {
    const swiperWrapper = element.querySelector<HTMLDivElement>('.swiper-wrapper')
    if (!swiperWrapper) return
    const wrapper = document.createElement('div')
    wrapper.classList.add('swiper-wrapper-container')
    wrapper.appendChild(swiperWrapper)
    element.appendChild(wrapper)
}

const createCarousel = (query: string, swiperOptions: SwiperOptions) => {
    const elements: NodeListOf<HTMLElement> = document.querySelectorAll(query)
    elements.forEach((element: HTMLElement) => {
        element.classList.add('swiper')
        
        const children = [...element.children]
        children.forEach((child: HTMLElement) => {
            child.classList.add('swiper-slide')
        })

        if (swiperOptions.navigation !== false) createNavigation(element)
        if (swiperOptions.pagination !== false) createPagination(element)

        const swiper = new Swiper(element, {
            modules: [ Navigation, Pagination, Autoplay ],
            createElements: true,
            watchOverflow: true,
            watchSlidesProgress: true,
            navigation: {
                prevEl: '.swiper-navigation__button-prev',
                nextEl: '.swiper-navigation__button-next',
            },
            pagination: {
                el: '.swiper-pagination',
                clickable: true,
                bulletElement: 'button',
            },
            ...swiperOptions,
        })

        createWrapper(element)

        const swiperNavigation = element.querySelector<HTMLDivElement>('.swiper-navigation')
        const swiperPagination = element.querySelector<HTMLDivElement>('.swiper-pagination')
        if (!swiperNavigation || !swiperPagination) return
        element.appendChild(swiperNavigation)
        element.appendChild(swiperPagination)
    })
}

const createHomepageCarousel = () => {
    createCarousel(".corp-home-page-hero", {
        effect: "fade",
        loop: true,
        navigation: false,
        pagination: false,
        autoplay: {
            delay: 5000,
            disableOnInteraction: false,
            pauseOnMouseEnter: true,
        },
    })
}

const createScrollingCtaCarousel = () => {
    createCarousel('.corp-scrolling-cta-banner', {
        autoplay: {
            delay: 5000,
            disableOnInteraction: false,
            pauseOnMouseEnter: true,
        },
        loop: true,
        grabCursor: true,
    })
}

const createListWidgetCarousel = () => {
    createCarousel('.jobs-carousel .attrax-list-widget__list--has-items', {
        slidesPerView: 1,
        spaceBetween: 28,
        breakpoints: {
            666: {
                slidesPerView: 2,
            },
            991: {
                slidesPerView: 3,
            },
            1200: {
                slidesPerView: 4,
            },
        },
        on: {
            init: (swiper) => {
                createCallToAction(swiper)
            }
        }
    })
}

const createPostsCarousel = () => {
    createCarousel('.post-carousel .attrax-list-widget__list--has-items', {
        slidesPerView: 1,
        spaceBetween: 75,
        breakpoints: {
            0: {
                slidesPerView: 1,
            },
            666: {
                slidesPerView: 2,
            },
            991: {
                slidesPerView: 3,
            },
        },
        on: {
            init: (swiper) => {
                createCallToAction(swiper)
            }
        }
    })
}

const createQuotesCarousel = () => {
    createCarousel(".carousel-quotes .banner-listings-wrapper", {
        slidesPerView: 1,
        spaceBetween: 64,
        grabCursor: true,
        loop: true,
        breakpoints: {
            640: {
                slidesPerView: 2,
            },
            990: {
                slidesPerView: 3,
                spaceBetween: 70,
            },
        },
    })
}

const createBrandsCarousel = () => {
    createCarousel('.corp-brands-banner', {
        loop: true,
        autoplay: {            
            delay: 3000,
            disableOnInteraction: false,
        },
        grabCursor: true,
        slidesPerView: 2,
        spaceBetween: 10,
        breakpoints: {
            320: {
                slidesPerView: 3,
            },
            420: {
                slidesPerView: 4,
            },
            576: {
                slidesPerView: 5,
            },
            768: {
                slidesPerView: 6,
                spaceBetween: 33,
            },
            991: {
                slidesPerView: 7,
            },
            1200: {
                slidesPerView: 8,
            },
            1300: {
                slidesPerView: 9,
            },
            1450: {
                slidesPerView: 10,
            },
        },
    })
}

export const CreateCarousels = () => {
    createHomepageCarousel()
    createBrandsCarousel()
    createListWidgetCarousel()
    createPostsCarousel()
    createQuotesCarousel()
    createScrollingCtaCarousel()
}
